import {
  ORDER_TYPES,
  ORDER_TYPES_DISPLAY_ORDER,
  PRODUCT_ACCESS_KEYS,
  PRODUCT_KEYS,
  PRODUCT_LABELS,
  REQUESTED_PRODUCT_KEYS,
  SALES_EMAIL,
  SALES_MAILTO,
  VALET_KEY_OPERATIONAL_MODE,
  VALET_PRODUCT_KEYS,
  actionLabel,
  assembleLocalInputs,
  emptyLocalProductData,
  prettifyKey,
  reasonText,
  resolveMovesLabel,
  salesMailto,
  withEmptyOnFailure
} from "./chunk-S6FSOA4D.mjs";

// src/host/PortalHostProvider.tsx
import React2 from "react";

// src/usePortalProducts.ts
import React from "react";

// src/heldValetModes.ts
import { PRODUCT_KEY_OPERATIONAL_MOVES } from "@hopdrive/sdk-products/catalog";
var CONTRACT_EXPIRED = "contract_expired";
var HELD_STATES = /* @__PURE__ */ new Set(["available", "limited", "read_only"]);
var EMPTY_HELD_VALET_MODES = { keys: [], modes: /* @__PURE__ */ new Set(), hasOperationalMoves: false };
var ENTITLED_STATES = /* @__PURE__ */ new Set(["available", "limited"]);
function heldValetModes(snapshot) {
  return valetModesWhere(snapshot, inStates(HELD_STATES));
}
function entitledValetModes(snapshot) {
  return valetModesWhere(snapshot, inStates(ENTITLED_STATES));
}
function operationalValetModes(snapshot) {
  const inUse = entitledValetModes(snapshot);
  return inUse.keys.length > 0 ? inUse : valetModesWhere(snapshot, isLapsedContract);
}
var EMPTY_VALET_MODE_SETS = { held: EMPTY_HELD_VALET_MODES, operational: EMPTY_HELD_VALET_MODES };
function valetModeSets(snapshot) {
  return { held: heldValetModes(snapshot), operational: operationalValetModes(snapshot) };
}
var inStates = (states) => (envelope) => states.has(envelope.state);
var isLapsedContract = (envelope) => envelope.state === "read_only" && envelope.reason?.key === CONTRACT_EXPIRED;
function valetModesWhere(snapshot, counts) {
  const keys = [];
  const modes = /* @__PURE__ */ new Set();
  for (const key of VALET_PRODUCT_KEYS) {
    const envelope = snapshot?.products?.[key];
    if (envelope && counts(envelope)) {
      keys.push(key);
      modes.add(VALET_KEY_OPERATIONAL_MODE[key]);
    }
  }
  return { keys, modes, hasOperationalMoves: keys.includes(PRODUCT_KEY_OPERATIONAL_MOVES) };
}

// src/capabilityGate.ts
var DEFAULT_DISABLED_REASON = "Not available for this product right now.";
var IN_USE_STATES = /* @__PURE__ */ new Set(["available", "limited"]);
function isInUse(state) {
  return IN_USE_STATES.has(state);
}
var RENDERABLE_STATES = {
  createNew: IN_USE_STATES,
  manageExisting: IN_USE_STATES,
  configure: IN_USE_STATES,
  viewHistory: /* @__PURE__ */ new Set(["available", "limited", "read_only"])
};
function hasCapability(entry, capability) {
  return capability === "createNew" ? entry.canCreate : entry.capabilities[capability] === true;
}
function capabilityGate(entry, capability, fallbackReason) {
  if (!entry || !RENDERABLE_STATES[capability].has(entry.state)) return { kind: "absent" };
  if (hasCapability(entry, capability)) return { kind: "render" };
  if (isInUse(entry.state)) {
    return { kind: "disabled", reason: reasonText(entry.reason) ?? fallbackReason ?? DEFAULT_DISABLED_REASON };
  }
  return { kind: "absent" };
}

// src/operational/combinedCreate.ts
function anyHeldCanCreate(envelopes, keys) {
  return keys.some((key) => {
    const envelope = envelopes?.[key];
    return envelope !== void 0 && isInUse(envelope.state) && envelope.capabilities.createNew === true;
  });
}

// src/strategies/combinators.ts
function always(value) {
  return () => value;
}
function allOf(...facts) {
  return (ctx) => facts.every((fact) => fact(ctx));
}

// src/signals/customerConfig.ts
function readDottedPath(value, path) {
  let current = value;
  for (const segment of path.split(".")) {
    if (current === null || typeof current !== "object") return void 0;
    current = current[segment];
  }
  return current;
}
function configFlagIsOn(config, path) {
  return readDottedPath(config, path) === true;
}
function plannerIsUnlocked(config) {
  if (config.add_moves_always === true) return true;
  return (config.add_moves_disabled ?? false) === false;
}

// src/strategies/perOrderChoiceWhen.ts
function perOrderChoiceWhen(flagPath, choice, otherwise) {
  return (ctx) => configFlagIsOn(ctx.inputs.config, flagPath) ? choice : otherwise;
}

// src/strategies/permission.ts
function permission(key) {
  return (ctx) => ctx.inputs.permissions[key];
}

// src/strategies/plannerUnlocked.ts
function plannerUnlocked() {
  return (ctx) => plannerIsUnlocked(ctx.inputs.config);
}

// src/operational/valetOperationalModel.ts
function resolveValetOperationalModel(modes) {
  const selfManaged = modes.has("self-managed");
  const hopdriveManaged = modes.has("hopdrive-managed");
  if (selfManaged && hopdriveManaged) return "mixed";
  if (selfManaged) return "self-managed";
  return "hopdrive-managed";
}
function isSelfManagedCapable(model) {
  return model === "self-managed" || model === "mixed";
}

// src/operational/registry.ts
var moves = {
  key: "moves",
  kind: "order-type",
  operationalModel: {
    default: (ctx) => resolveValetOperationalModel(ctx.valetModes.operational.modes),
    perOrderChoice: always("moves.lite")
  },
  accessGranted: always(true),
  viewerCanCreate: allOf(permission("canPlanMoves"), plannerUnlocked())
};
var guestRides = {
  key: "guest-rides",
  kind: "order-type",
  operationalModel: { default: always("hopdrive-managed"), perOrderChoice: always("none") },
  accessGranted: always(true),
  viewerCanCreate: always(true)
};
var mobileService = {
  key: "mobile-service",
  kind: "order-type",
  operationalModel: { default: always("self-managed"), perOrderChoice: always("none") },
  accessGranted: permission("hasMobileServiceAccess"),
  viewerCanCreate: always(true)
};
var partsDelivery = {
  key: "parts-delivery",
  kind: "order-type",
  operationalModel: {
    default: always("self-managed"),
    perOrderChoice: perOrderChoiceWhen("parts_delivery.third_party", "fulfillment_type", "none")
  },
  accessGranted: permission("hasPartsDeliveryAccess"),
  viewerCanCreate: permission("hasPartsDeliveryAccess")
};
var appraisals = {
  key: "appraisals",
  kind: "stored-request",
  operationalModel: null,
  accessGranted: permission("hasAppraisalsAccess"),
  viewerCanCreate: permission("hasAppraisalsAccess")
};
var PRODUCT_PORTAL_DEFINITIONS = {
  moves,
  "guest-rides": guestRides,
  "mobile-service": mobileService,
  "parts-delivery": partsDelivery,
  appraisals
};

// src/signals/context.ts
function buildContext(inputs, valetModes = EMPTY_VALET_MODE_SETS) {
  return { inputs, valetModes };
}

// src/compose.ts
var HIDDEN_CAPABILITIES = {
  visible: false,
  viewHistory: false,
  manageExisting: false,
  createNew: false,
  configure: false
};
var STATE_RANK = {
  available: 0,
  limited: 1,
  read_only: 2,
  marketing: 3,
  hidden: 4
};
function selectEnvelope(snapshot, key) {
  let best;
  for (const productKey of PRODUCT_ACCESS_KEYS[key]) {
    const envelope = snapshot?.products?.[productKey];
    if (!envelope) continue;
    if (!best || STATE_RANK[envelope.state] < STATE_RANK[best.state]) best = envelope;
  }
  return best;
}
function entryCreateNew(snapshot, envelope, key) {
  if (key === "moves") return anyHeldCanCreate(snapshot?.products, PRODUCT_ACCESS_KEYS.moves);
  return envelope?.capabilities.createNew === true;
}
function composeEntry(snapshot, key, inputs, valetModes) {
  const definition = PRODUCT_PORTAL_DEFINITIONS[key];
  const ctx = buildContext(inputs, valetModes);
  const envelope = selectEnvelope(snapshot, key);
  const capabilities = envelope?.capabilities ?? HIDDEN_CAPABILITIES;
  const probe = inputs.history[key];
  return {
    state: envelope?.state ?? "hidden",
    capabilities,
    reason: envelope?.reason,
    actions: envelope?.actions,
    context: envelope?.context,
    operationalModel: definition.operationalModel === null ? null : {
      default: definition.operationalModel.default(ctx),
      perOrderChoice: definition.operationalModel.perOrderChoice(ctx)
    },
    heldModes: key === "moves" ? valetModes.held : null,
    hasHistory: probe?.hasOrders ?? false,
    lastOrderAt: probe?.lastOrderAt ?? null,
    accessGranted: definition.accessGranted(ctx),
    canCreate: entryCreateNew(snapshot, envelope, key) && definition.viewerCanCreate(ctx)
  };
}
function composePortalProducts(snapshot, inputs, resolvedAt) {
  const valetModes = valetModeSets(snapshot);
  const entries = {};
  for (const key of PRODUCT_KEYS) {
    entries[key] = composeEntry(snapshot, key, inputs, valetModes);
  }
  return { customerId: inputs.customerId, resolvedAt, entries };
}

// src/deriveProducts.ts
function deriveProducts(inputs, permissions) {
  return composePortalProducts(inputs.snapshot, { ...inputs.local, permissions }, inputs.fetchedAt);
}

// src/gql/getPortalProductInputs.query.generated.ts
import { resolveExecutor } from "@hopdrive/sdk-core";
var GetPortalProductInputsDataDocument = { "__text": 'query GetPortalProductInputsData($customerId: bigint!) {\n  customers_by_pk(id: $customerId) {\n    id\n    effective_config\n  }\n  moves: moves(\n    where: {_or: [{customer_id: {_eq: $customerId}}, {payer_id: {_eq: $customerId}}]}\n    order_by: {pickup_time: desc_nulls_last}\n    limit: 1\n  ) {\n    id\n    pickup_time\n  }\n  mobileService: mobility_runs(\n    where: {customer_id: {_eq: $customerId}, product_type: {_eq: "mobile_service"}}\n    order_by: {departure_time: desc_nulls_last}\n    limit: 1\n  ) {\n    id\n    departure_time\n  }\n  partsDelivery: mobility_runs(\n    where: {customer_id: {_eq: $customerId}, product_type: {_eq: "parts_delivery"}}\n    order_by: {departure_time: desc_nulls_last}\n    limit: 1\n  ) {\n    id\n    departure_time\n  }\n  guestRides: guestrides(\n    where: {customer_id: {_eq: $customerId}}\n    order_by: {created_at: desc_nulls_last}\n    limit: 1\n  ) {\n    id\n    created_at\n  }\n  appraisals(\n    where: {customer_id: {_eq: $customerId}}\n    order_by: {last_save_time_utc: desc_nulls_last}\n    limit: 1\n  ) {\n    id\n    last_save_time_utc\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetPortalProductInputsData" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "customers_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "moves" }, "name": { "kind": "Name", "value": "moves" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_or" }, "value": { "kind": "ListValue", "values": [{ "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] }, { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "payer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_time" }, "value": { "kind": "EnumValue", "value": "desc_nulls_last" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_time" } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "mobileService" }, "name": { "kind": "Name", "value": "mobility_runs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "product_type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "mobile_service", "block": false } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "departure_time" }, "value": { "kind": "EnumValue", "value": "desc_nulls_last" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departure_time" } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "partsDelivery" }, "name": { "kind": "Name", "value": "mobility_runs" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "product_type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "parts_delivery", "block": false } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "departure_time" }, "value": { "kind": "EnumValue", "value": "desc_nulls_last" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "departure_time" } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "guestRides" }, "name": { "kind": "Name", "value": "guestrides" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "created_at" }, "value": { "kind": "EnumValue", "value": "desc_nulls_last" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "created_at" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "appraisals" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "order_by" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "last_save_time_utc" }, "value": { "kind": "EnumValue", "value": "desc_nulls_last" } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "last_save_time_utc" } }] } }] } }] };
async function getPortalProductInputsData(variables, options) {
  const { executor, ...requestOptions } = options ?? {};
  return resolveExecutor(executor).query(
    GetPortalProductInputsDataDocument,
    variables,
    requestOptions
  );
}

// src/snapshot/productAccessGql.ts
import { gql } from "@apollo/client";
var GET_PRODUCT_ACCESS = gql`
  query GetProductAccess($customerId: Int!, $products: [String!]) {
    getProductAccess(customerId: $customerId, products: $products) {
      subject
      evaluatedAt
      products
    }
  }
`;

// src/earlyProducts.ts
var inflight;
function startEarlyProductsFetch(run, token, customerId, today = (/* @__PURE__ */ new Date()).toISOString()) {
  const access = run({
    query: GET_PRODUCT_ACCESS,
    variables: { customerId, products: [...REQUESTED_PRODUCT_KEYS] },
    token
  }).then((r) => r?.getProductAccess ?? null);
  const local = withEmptyOnFailure(
    run({
      query: GetPortalProductInputsDataDocument,
      variables: { customerId },
      token
    }).then((data) => assembleLocalInputs(data, customerId)),
    customerId
  );
  const result = Promise.all([access, local]);
  result.catch(() => void 0);
  inflight = { customerId, today, result };
  return inflight;
}
function takeEarlyProductsFetch(customerId) {
  if (!inflight || inflight.customerId !== customerId) return void 0;
  const taken = inflight;
  inflight = void 0;
  return taken;
}
function resetEarlyProductsFetch() {
  inflight = void 0;
}

// src/productSnapshotStore.ts
var PREFIX = "portalProducts:v1:";
var SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1e3;
function storage() {
  try {
    return typeof localStorage === "undefined" ? void 0 : localStorage;
  } catch {
    return void 0;
  }
}
function snapshotKey(userId, customerId) {
  return `${PREFIX}${userId}:${customerId}`;
}
function readProductSnapshot(userId, customerId, now = Date.now()) {
  if (!userId || !customerId) return void 0;
  const s = storage();
  const key = snapshotKey(userId, customerId);
  const raw = s?.getItem(key);
  if (!raw) return void 0;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.storedAt !== "number" || !parsed.inputs) throw new Error("shape");
    if (now - parsed.storedAt > SNAPSHOT_MAX_AGE_MS || now < parsed.storedAt) {
      s?.removeItem(key);
      return void 0;
    }
    return parsed.inputs;
  } catch {
    s?.removeItem(key);
    return void 0;
  }
}
function writeProductSnapshot(userId, customerId, inputs, now = Date.now()) {
  if (!userId || !customerId) return;
  try {
    storage()?.setItem(snapshotKey(userId, customerId), JSON.stringify({ storedAt: now, inputs }));
  } catch {
  }
}

// src/store.ts
import { create } from "zustand";
var INITIAL = {
  productInputs: void 0,
  portalProducts: void 0,
  productsLoaded: false,
  productsFailed: false,
  productsRetryNonce: 0
};
var portalProductsStore = create(() => ({ ...INITIAL }));
var useProductInputs = () => portalProductsStore((s) => s.productInputs);
var setProductInputs = (productInputs) => portalProductsStore.setState({ productInputs });
var usePortalProductsState = () => portalProductsStore((s) => s.portalProducts);
var setPortalProducts = (portalProducts) => portalProductsStore.setState({ portalProducts });
var useProductsLoaded = () => portalProductsStore((s) => s.productsLoaded);
var setProductsLoaded = (productsLoaded) => portalProductsStore.setState({ productsLoaded });
var useProductsFailed = () => portalProductsStore((s) => s.productsFailed);
var setProductsFailed = (productsFailed) => portalProductsStore.setState({ productsFailed });
var useProductsRetryNonce = () => portalProductsStore((s) => s.productsRetryNonce);
var retryProducts = () => portalProductsStore.setState((s) => ({ productsRetryNonce: s.productsRetryNonce + 1 }));
var resetPortalProducts = () => portalProductsStore.setState({ ...INITIAL });

// src/usePortalProducts.ts
var NO_CUSTOMER_REPORT_MS = 1e4;
function usePortalProducts() {
  const { viewer, fetchers, report } = usePortalHost();
  const { customerId, viewerId, role, productPermissions: permissions } = viewer;
  const inputs = useProductInputs();
  const retryNonce = useProductsRetryNonce();
  const requestToken = React.useRef(0);
  const permissionsRef = React.useRef(permissions);
  permissionsRef.current = permissions;
  const fetchersRef = React.useRef(fetchers);
  fetchersRef.current = fetchers;
  const reportRef = React.useRef(report);
  reportRef.current = report;
  const roleRef = React.useRef(role);
  roleRef.current = role;
  React.useEffect(() => {
    const token = ++requestToken.current;
    setProductInputs(void 0);
    setPortalProducts(void 0);
    setProductsFailed(false);
    if (customerId === void 0) {
      setProductsLoaded(true);
      if (viewerId === void 0) return;
      const timer = setTimeout(() => {
        reportRef.current?.("portal_products.no_customer", {
          viewerId,
          role: roleRef.current ?? null,
          waitedMs: NO_CUSTOMER_REPORT_MS
        });
      }, NO_CUSTOMER_REPORT_MS);
      return () => clearTimeout(timer);
    }
    const stored = readProductSnapshot(viewerId, customerId);
    if (stored) {
      setProductInputs(stored);
      setPortalProducts(deriveProducts(stored, permissionsRef.current));
      setProductsLoaded(true);
    } else {
      setProductsLoaded(false);
    }
    const early = takeEarlyProductsFetch(customerId);
    const today = early?.today ?? (/* @__PURE__ */ new Date()).toISOString();
    const pending = early?.result ?? Promise.all([
      fetchersRef.current.fetchProductAccess(customerId),
      withEmptyOnFailure(fetchersRef.current.fetchLocalInputs(customerId), customerId)
    ]);
    pending.then(([snapshot, local]) => {
      if (token !== requestToken.current) return;
      const fresh = { snapshot, local, fetchedAt: today };
      setProductInputs(fresh);
      setPortalProducts(deriveProducts(fresh, permissionsRef.current));
      setProductsLoaded(true);
      writeProductSnapshot(viewerId, customerId, fresh);
    }).catch((err) => {
      if (token !== requestToken.current) return;
      console.error("Failed to load the portal products", err);
      const facts = { customerId, viewerId: viewerId ?? null, role: roleRef.current ?? null, retry: retryNonce > 0 };
      if (!stored) {
        setProductsFailed(true);
        reportRef.current?.("portal_products.fetch_failed", facts, err);
      } else {
        reportRef.current?.("portal_products.refresh_failed", facts, err);
      }
      setProductsLoaded(true);
    });
  }, [customerId, viewerId, retryNonce]);
  React.useEffect(() => {
    if (inputs) setPortalProducts(deriveProducts(inputs, permissions));
  }, [inputs, permissions]);
}

// src/host/PortalHostProvider.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var PortalHostContext = React2.createContext(null);
var PortalHostProvider = ({ value, children }) => /* @__PURE__ */ jsxs(PortalHostContext.Provider, { value, children: [
  /* @__PURE__ */ jsx(PortalProductsLoader, {}),
  children
] });
var PortalProductsLoader = () => {
  usePortalProducts();
  return null;
};
function usePortalHost() {
  const host = React2.useContext(PortalHostContext);
  if (!host) throw new Error("usePortalHost must be used inside <PortalHostProvider>");
  return host;
}
function usePortalViewer() {
  return usePortalHost().viewer;
}
function usePortalRouter() {
  return usePortalHost().router;
}
function useViewerFeature(...names) {
  const { grantedFeatures, permissionsLoaded } = usePortalViewer();
  return permissionsLoaded && names.some((name) => grantedFeatures.has(name));
}

// src/useProductEntry.ts
function useProductEntry(key) {
  return usePortalProductsState()?.entries[key];
}

// src/useValetSelfManaged.ts
function useValetSelfManaged() {
  return isSelfManagedCapable(useProductEntry("moves")?.operationalModel?.default);
}

// src/productGates.ts
function isProductShown(entry) {
  return entry !== void 0 && entry.capabilities.visible === true && entry.accessGranted;
}
function isProductAccessGranted(entry) {
  return entry !== void 0 && entry.accessGranted;
}
function isProductHeld(entry) {
  return entry !== void 0 && entry.capabilities.visible === true && entry.state !== "marketing";
}
function isProductOwned(entry) {
  return isProductHeld(entry) && isProductAccessGranted(entry);
}
function isProductOffered(entry) {
  return entry !== void 0 && isProductShown(entry) && entry.state === "marketing";
}
function isProductRouteMounted(entry) {
  return entry !== void 0 && entry.capabilities.visible === true;
}

// src/snapshot/createApolloProductFetchers.ts
function createApolloProductFetchers(client) {
  return {
    /** The customer's product-access snapshot (the getProductAccess Hasura action). Returns
     *  null when the action returns nothing; throws on transport errors, which the caller
     *  settles fail-closed. */
    async fetchProductAccess(customerId) {
      const variables = { customerId, products: [...REQUESTED_PRODUCT_KEYS] };
      const { data } = await client.query({
        query: GET_PRODUCT_ACCESS,
        variables: { ...variables },
        fetchPolicy: "network-only"
      });
      return data?.getProductAccess ?? null;
    },
    /** Fetch and assemble the app-side product inputs for one customer. */
    async fetchLocalInputs(customerId) {
      const variables = { customerId };
      const { data } = await client.query({
        query: GetPortalProductInputsDataDocument,
        variables: { ...variables },
        fetchPolicy: "network-only"
      });
      return assembleLocalInputs(data, customerId);
    }
  };
}

// src/settings/createApolloConfigServicePort.ts
import { gql as gql2 } from "@apollo/client";
function isMutation(parsed) {
  return parsed.definitions.some(
    (definition) => definition.kind === "OperationDefinition" && definition.operation === "mutation"
  );
}
function createApolloConfigServicePort(client) {
  const resolveClient = async () => typeof client === "function" ? client() : client;
  return {
    request: async (document, variables) => {
      const resolved = await resolveClient();
      const parsed = gql2(document);
      if (isMutation(parsed)) {
        const result2 = await resolved.mutate({ mutation: parsed, variables });
        return result2.data;
      }
      const result = await resolved.query({ query: parsed, variables, fetchPolicy: "network-only" });
      return result.data;
    }
  };
}

// src/snapshot/useRooftopProductSnapshot.ts
import React3 from "react";
var NO_ROOFTOP = { snapshot: null, loading: false };
function useRooftopProductSnapshot(rooftopId) {
  const workingRooftopId = usePortalViewer().customerId;
  const { fetchers } = usePortalHost();
  const fetchersRef = React3.useRef(fetchers);
  fetchersRef.current = fetchers;
  const storeSnapshot = useProductInputs()?.snapshot ?? null;
  const productsLoaded = useProductsLoaded();
  const hasRooftop = typeof rooftopId === "number" && rooftopId > 0;
  const storeCovers = hasRooftop && (rooftopId === workingRooftopId || storeSnapshot?.subject.id === rooftopId);
  const [fetched, setFetched] = React3.useState(null);
  React3.useEffect(() => {
    if (!hasRooftop || storeCovers) return;
    let cancelled = false;
    fetchersRef.current.fetchProductAccess(rooftopId).then((snapshot) => {
      if (!cancelled) setFetched({ rooftopId, snapshot });
    }).catch((err) => {
      console.error(`useRooftopProductSnapshot: could not load the product access for rooftop ${rooftopId}`, err);
      if (!cancelled) setFetched({ rooftopId, snapshot: null });
    });
    return () => {
      cancelled = true;
    };
  }, [rooftopId, hasRooftop, storeCovers]);
  return React3.useMemo(() => {
    if (!hasRooftop) return NO_ROOFTOP;
    if (storeCovers) return { snapshot: storeSnapshot, loading: storeSnapshot === null && !productsLoaded };
    if (fetched?.rooftopId === rooftopId) return { snapshot: fetched.snapshot, loading: false };
    return { snapshot: null, loading: true };
  }, [hasRooftop, storeCovers, storeSnapshot, productsLoaded, fetched, rooftopId]);
}

// src/snapshot/snapshotFacts.ts
function snapshotSelfManaged(snapshot) {
  return isSelfManagedCapable(resolveValetOperationalModel(entitledValetModes(snapshot).modes));
}
function snapshotCanCreate(snapshot, productKey) {
  const envelope = snapshot.products[productKey];
  return envelope ? envelope.capabilities.createNew === true : true;
}
function snapshotMovesReason(snapshot) {
  return reasonText(selectEnvelope(snapshot, "moves")?.reason) ?? void 0;
}
function snapshotFacts(snapshot, loading) {
  return {
    known: snapshot !== null,
    loading,
    heldValetKeys: entitledValetModes(snapshot).keys,
    selfManaged: snapshotSelfManaged(snapshot),
    canCreate: snapshot ? (productKey) => snapshotCanCreate(snapshot, productKey) : void 0,
    reason: snapshotMovesReason(snapshot)
  };
}

// src/snapshot/useRooftopProductFacts.ts
import React4 from "react";
function useRooftopProductFacts(rooftopId) {
  const { snapshot, loading } = useRooftopProductSnapshot(rooftopId);
  return React4.useMemo(() => snapshotFacts(snapshot, loading), [snapshot, loading]);
}

// src/shell/ProductShellLayout.tsx
import { useCallback as useCallback3, useMemo as useMemo3, useState as useState3 } from "react";
import { useTheme } from "@mui/material";

// src/actions/ProductCreationDialogs.tsx
import { Fragment } from "react";
import { Fragment as Fragment2, jsx as jsx2 } from "react/jsx-runtime";
var ProductCreationDialogs = ({ definition, openKey, onClose, onCreated }) => {
  if (!definition) return null;
  const dialogs = [...definition.creation, ...definition.secondary ? [definition.secondary] : []];
  return /* @__PURE__ */ jsx2(Fragment2, { children: dialogs.map(
    (item) => item.dialog ? /* @__PURE__ */ jsx2(Fragment, { children: item.dialog({ open: openKey === item.key, onClose, onCreated }) }, item.key) : null
  ) });
};
var ProductCreationDialogs_default = ProductCreationDialogs;

// src/actions/resolveHeaderActions.ts
var productActionsShown = (entry) => entry !== void 0 && capabilityGate(entry, "createNew").kind !== "absent";
var creationDisabledReason = (entry, fallback) => {
  const treatment = capabilityGate(entry, "createNew", fallback);
  return treatment.kind === "disabled" ? treatment.reason : void 0;
};
var toShellAction = (item, disabledReason, open) => ({
  key: item.key,
  label: item.label,
  icon: item.icon,
  disabledReason,
  ...item.to ? { to: item.to } : { onSelect: () => open(item.key) }
});
function resolveHeaderActions({
  definition,
  entry,
  tab,
  open
}) {
  const model = {};
  if (definition && productActionsShown(entry)) {
    const disabledReason = creationDisabledReason(entry, definition.createUnavailableText);
    const methods = definition.creation.map((method) => toShellAction(method, disabledReason, open));
    if (methods.length > 0) {
      model.primary = { label: definition.menuLabel ?? methods[0].label, icon: definition.menuIcon, methods };
    }
    if (definition.secondary) model.secondary = toShellAction(definition.secondary, void 0, open);
  }
  if (tab?.sync) model.sync = { onSync: tab.sync };
  if (tab?.overflow && tab.overflow.length > 0) model.overflow = tab.overflow;
  return model;
}

// src/actions/withSyncFeedback.ts
function withSyncFeedback(sync, notify) {
  if (!sync) return void 0;
  return async () => {
    try {
      await sync();
      notify.info("Data refetched!");
    } catch (error) {
      notify.error("Error refetching data!");
      console.error("Error refetching data:", error);
    }
  };
}

// src/settings/useConfigServiceAdapter.ts
import { useMemo } from "react";

// src/settings/configRole.ts
var CONFIG_ROLES = /* @__PURE__ */ new Set([
  "admin",
  "dispatcher",
  "driver",
  "consumer",
  "dealer-admin",
  "dealer",
  "dealer_user",
  "dealer_manager",
  "dealer_admin",
  "dealer-super-admin",
  "pending-driver"
]);
function toConfigRole(hasuraRole) {
  if (!hasuraRole) return void 0;
  return CONFIG_ROLES.has(hasuraRole) ? hasuraRole : void 0;
}

// src/settings/useConfigServiceAdapter.ts
function useConfigServiceAdapter() {
  const { viewer, settings } = usePortalHost();
  const role = toConfigRole(viewer.role);
  const { request, createAdapter } = settings;
  const adapter = useMemo(() => createAdapter(request, { role }), [createAdapter, request, role]);
  const override = settings.target;
  const customerId = viewer.customerId;
  const target = useMemo(() => {
    if (override !== void 0) return override;
    return customerId == null ? null : { level: "customer", levelId: customerId };
  }, [override, customerId]);
  return { adapter, target };
}

// src/shell/accessFallbacks.tsx
import { Button } from "@mui/material";
import { jsx as jsx3 } from "react/jsx-runtime";
var RestrictedAccessFallback = () => {
  const { Fallback } = usePortalHost().components;
  return /* @__PURE__ */ jsx3(Fallback, { fullscreen: true, severity: "error", message: "RESTRICTED ACCESS" });
};
var ProductsFailedFallback = () => {
  const { Fallback } = usePortalHost().components;
  return /* @__PURE__ */ jsx3(
    Fallback,
    {
      fullscreen: true,
      severity: "error",
      message: "We couldn\u2019t load your products. Please try again.",
      action: /* @__PURE__ */ jsx3(Button, { color: "inherit", size: "small", onClick: retryProducts, children: "Retry" })
    }
  );
};

// src/shell/featureAccess.ts
function isProductsFinal({ productsLoaded, productsPresent, productsFailed }) {
  return productsLoaded && (productsPresent || productsFailed);
}
function isPortalAccessSettled(state) {
  return state.hostReady && isProductsFinal(state);
}
function resolveFeatureAccess(input) {
  if (input.available) return "allowed";
  if (!isPortalAccessSettled(input)) return "pending";
  if (input.productsFailed) return "failed";
  return "restricted";
}

// src/shell/productAdvertiseContent.ts
import { createElement } from "react";
import {
  BiBarChartAlt2,
  BiCalendarCheck,
  BiCamera,
  BiCar,
  BiCheckShield,
  BiDollarCircle,
  BiMapAlt,
  BiPackage,
  BiSolidEnvelope,
  BiSolidTruck,
  BiUndo,
  BiUserVoice
} from "react-icons/bi";

// src/shell/stateActionRouter.ts
function resolveStateAction(action, actionRoutes, salesMailto2) {
  const route = actionRoutes[action.type];
  return route ? { to: route } : { href: salesMailto2 };
}
var CONTACT_SALES_ACTION = { type: "contact_sales" };
function withContactSales(state, actions) {
  const own = actions ?? [];
  return state === "read_only" && own.length === 0 ? [CONTACT_SALES_ACTION] : own;
}
function followStateAction(destination, navigate) {
  if (destination.to) navigate(destination.to);
  else if (destination.href) window.location.assign(destination.href);
}
function openSalesContact(subject) {
  followStateAction({ href: subject ? salesMailto(subject) : SALES_MAILTO }, () => {
  });
}

// src/shell/productAdvertiseContent.ts
var emailRepCta = (product) => [
  { label: "Email a HopDrive Rep", icon: createElement(BiSolidEnvelope), onClick: () => openSalesContact(`${product} inquiry`) }
];
var PRODUCT_ADVERTISE_CONTENT = {
  moves: {
    headline: "Grow with Valet",
    lede: "Your drivers or HopDrive drivers move vehicles between your locations, tracked from pickup to delivery.",
    benefits: [
      {
        icon: createElement(BiCar),
        title: "Vehicle moves, handled",
        body: "Move vehicles between your locations with full tracking."
      },
      {
        icon: createElement(BiMapAlt),
        title: "Promise-time tracking",
        body: "Every move tracked from pickup to delivery on one board."
      },
      {
        icon: createElement(BiCheckShield),
        title: "Damage documentation",
        body: "Photos and inspection at each leg, kept as history."
      }
    ],
    ctas: emailRepCta("Valet")
  },
  "guest-rides": {
    headline: "Keep guests moving with Guest Rides",
    lede: "Rides for your customers while their vehicle is with you, requested by your team or by the guest.",
    benefits: [
      {
        icon: createElement(BiUserVoice),
        title: "Rides on demand",
        body: "Request a ride for a guest in seconds, or let them request it."
      },
      {
        icon: createElement(BiCar),
        title: "A shareable request code",
        body: "Guests book their own return ride from a code you share."
      },
      {
        icon: createElement(BiDollarCircle),
        title: "One invoice",
        body: "Rides settle against your credit line on a single statement."
      }
    ],
    ctas: emailRepCta("Guest Rides")
  },
  "mobile-service": {
    headline: "Bring the shop to the customer",
    lede: "Schedule on-site appointments and dispatch your technicians on planned service runs.",
    benefits: [
      {
        icon: createElement(BiCalendarCheck),
        title: "Scheduled appointments",
        body: "Book service at the customer and keep the day in view."
      },
      { icon: createElement(BiMapAlt), title: "Runs on a map", body: "Plan each technician run and follow it live." },
      {
        icon: createElement(BiSolidTruck),
        title: "Service vehicles tracked",
        body: "Know which vehicle is where, and who is driving it."
      }
    ],
    ctas: emailRepCta("Mobile Service")
  },
  "parts-delivery": {
    headline: "Grow with Parts Delivery",
    lede: "Group orders by destination, build the fastest route, and dispatch your own drivers or a HopDrive courier.",
    benefits: [
      {
        icon: createElement(BiPackage),
        title: "Orders to routes",
        body: "Group orders by destination and build the fastest route automatically."
      },
      {
        icon: createElement(BiSolidTruck),
        title: "Own drivers or courier",
        body: "Dispatch your own team or hand a run to a HopDrive-managed courier."
      },
      {
        icon: createElement(BiUndo),
        title: "Proof of delivery and returns",
        body: "Photo and signature capture at every stop, plus a returns workflow."
      }
    ],
    ctas: emailRepCta("Parts Delivery")
  },
  appraisals: {
    headline: "Appraise vehicles right off the lot",
    lede: "A guided walkaround captures condition and value in one record, kept per vehicle.",
    benefits: [
      {
        icon: createElement(BiCamera),
        title: "Guided walkaround",
        body: "Photos and condition notes captured step by step."
      },
      {
        icon: createElement(BiBarChartAlt2),
        title: "Value in one record",
        body: "Condition, value, and notes together on a single appraisal."
      },
      {
        icon: createElement(BiCheckShield),
        title: "History per vehicle",
        body: "Every appraisal stays on file and searchable."
      }
    ],
    ctas: emailRepCta("Appraisals")
  }
};

// src/ui/productIdentity.tsx
import { PiCarFill, PiCertificateFill, PiHandWavingFill, PiPackageFill, PiWrenchFill } from "react-icons/pi";
import { jsx as jsx4 } from "react/jsx-runtime";
var PRODUCT_IDENTITY = {
  moves: {
    name: PRODUCT_LABELS.moves,
    description: "Moves to and from your locations",
    icon: /* @__PURE__ */ jsx4(PiCarFill, {})
  },
  "guest-rides": {
    name: PRODUCT_LABELS["guest-rides"],
    description: "Rides for your guests, called by you or by them",
    icon: /* @__PURE__ */ jsx4(PiHandWavingFill, {})
  },
  "mobile-service": {
    name: PRODUCT_LABELS["mobile-service"],
    description: "Service runs your technicians complete at the customer",
    icon: /* @__PURE__ */ jsx4(PiWrenchFill, {})
  },
  "parts-delivery": {
    name: PRODUCT_LABELS["parts-delivery"],
    description: "Parts moved between your locations and service centers",
    icon: /* @__PURE__ */ jsx4(PiPackageFill, {})
  },
  appraisals: {
    name: PRODUCT_LABELS.appraisals,
    description: "Appraise vehicles right off the lot",
    icon: /* @__PURE__ */ jsx4(PiCertificateFill, {})
  }
};
function productIdentity(product) {
  return PRODUCT_IDENTITY[product];
}
function productName(product, heldModes) {
  return product === "moves" ? resolveMovesLabel(heldModes) : PRODUCT_IDENTITY[product].name;
}
var PRODUCT_DESCRIPTION_BY_MODEL = {
  moves: {
    "hopdrive-managed": "Moves driven by the HopDrive network",
    "self-managed": "Moves driven by your own drivers"
  }
};
function productDescription(product, model) {
  const byModel = model && model !== "mixed" ? PRODUCT_DESCRIPTION_BY_MODEL[product]?.[model] : void 0;
  return byModel || PRODUCT_IDENTITY[product].description;
}
function productAccentColor(palette) {
  const { utility } = palette;
  return utility.main;
}

// src/shell/resolveIdentity.ts
function resolveIdentity(product, entry, palette) {
  return {
    ...productIdentity(product),
    name: productName(product, entry?.heldModes),
    description: productDescription(product, entry?.operationalModel?.default),
    accentColor: productAccentColor(palette)
  };
}

// src/settings/productSettingsKeys.ts
var SETTINGS_TAB_KEY = "__settings";
var PRODUCT_SETTINGS_KEYS = {
  "guest-rides": ["guestrides"],
  appraisals: ["appraisals"]
};
function settingsKeysFor(product) {
  return PRODUCT_SETTINGS_KEYS[product] ?? [];
}
function productHasSettings(product) {
  return settingsKeysFor(product).length > 0;
}

// src/shell/settingsTab.ts
function settingsTabRoute(routes, product) {
  return { key: SETTINGS_TAB_KEY, label: "Settings", path: `${routes.productRoot[product]}/settings` };
}
function withSettingsTab(routes, product, tabs, hasTarget, canConfigure) {
  if (!hasTarget || !canConfigure || !productHasSettings(product)) return tabs;
  if (tabs.some((tab) => tab.key === SETTINGS_TAB_KEY)) return tabs;
  return [...tabs, settingsTabRoute(routes, product)];
}

// src/shell/resolveLayoutTabs.ts
function resolveLayoutTabs(routes, product, tabs, { canViewHistory, canConfigure, hasSettingsTarget }) {
  const visible = canViewHistory ? tabs : canConfigure && hasSettingsTarget ? tabs.filter((tab) => tab.key === SETTINGS_TAB_KEY) : [];
  return withSettingsTab(routes, product, visible, hasSettingsTarget, canConfigure);
}

// src/shell/shellFrameContext.ts
import { createContext, useContext } from "react";
var ShellFrameContext = createContext(void 0);
function useShellFrame() {
  return useContext(ShellFrameContext);
}

// src/shell/shellSlots.tsx
import { useCallback, useMemo as useMemo2, useState } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import { jsx as jsx5 } from "react/jsx-runtime";
var NO_ELEMENTS = { banner: null, aboveTabs: null };
var SlotHost = ({ onElement }) => /* @__PURE__ */ jsx5(Box, { ref: onElement, sx: { display: "contents" } });
function useShellSlotHosts() {
  const [slotElements, setSlotElements] = useState(NO_ELEMENTS);
  const bind = useCallback(
    (name) => (element) => setSlotElements((prev) => prev[name] === element ? prev : { ...prev, [name]: element }),
    []
  );
  const hosts = useMemo2(
    () => ({
      banner: /* @__PURE__ */ jsx5(SlotHost, { onElement: bind("banner") }),
      aboveTabs: /* @__PURE__ */ jsx5(SlotHost, { onElement: bind("aboveTabs") })
    }),
    [bind]
  );
  const frameContext = useMemo2(() => ({ slotElements }), [slotElements]);
  return { hosts, frameContext };
}
var ShellSlot = ({ slot, children }) => {
  const element = useShellFrame()?.slotElements[slot] ?? null;
  return element ? createPortal(children, element) : null;
};

// src/shell/shellTabActions.tsx
import { useCallback as useCallback2, useEffect, useState as useState2 } from "react";
function useTabActionsHost() {
  const [contribution, setContribution] = useState2(null);
  return { contribution, setContribution };
}
function useShellTabActions(contribution) {
  const setTabActions = useShellFrame()?.setTabActions;
  useEffect(() => {
    if (!setTabActions) return void 0;
    setTabActions(contribution);
    return () => setTabActions(null);
  }, [setTabActions, contribution]);
}
function useOpenProductAction() {
  const openProductAction = useShellFrame()?.openProductAction;
  return useCallback2((key) => openProductAction?.(key), [openProductAction]);
}

// src/shell/tabRoutes.ts
function isMoveDetailSegment(segment) {
  return segment !== void 0 && /^\d+$/.test(segment) && Number.isSafeInteger(Number(segment));
}
var withoutTrailingSlash = (pathname) => pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
var pathIsUnder = (prefix, pathname) => pathname === prefix || pathname.startsWith(`${prefix}/`);
function resolveActiveTabKey(tabs, pathname) {
  const path = withoutTrailingSlash(pathname);
  let best;
  for (const tab of tabs) {
    for (const prefix of [tab.path, ...tab.matches ?? []]) {
      if (pathIsUnder(prefix, path) && (best === void 0 || prefix.length > best.length)) {
        best = { key: tab.key, length: prefix.length };
      }
    }
  }
  return best?.key;
}
function tabPath(tabs, key) {
  return tabs.find((tab) => tab.key === key)?.path;
}
function unmountedTabs(candidates, tabs) {
  return candidates.filter((candidate) => !tabs.some((tab) => tab.key === candidate.key));
}
function toShellTabs(tabs) {
  return tabs.map(({ key, label, badge, disabled }) => ({ key, label, badge, disabled }));
}

// src/shell/toShellAccess.ts
function toShellAccess(entry) {
  if (!entry) return { state: "hidden", capabilities: { ...HIDDEN_CAPABILITIES }, canCreate: false };
  return {
    state: entry.state,
    capabilities: { ...HIDDEN_CAPABILITIES, ...entry.capabilities },
    reason: entry.reason,
    actions: entry.actions,
    canCreate: entry.canCreate
  };
}

// src/shell/ProductShellLayout.tsx
import { Fragment as Fragment3, jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var ProductShellLayout = ({
  product,
  tabs,
  marketing,
  actions,
  banner,
  suppressStateBanner
}) => {
  const { routing, router, components, notify } = usePortalHost();
  const location = router.useLocation();
  const navigate = router.useNavigate();
  const { Link, Outlet } = router;
  const ProductShell = components.ProductShell;
  const loaded = useProductsLoaded();
  const products = usePortalProductsState();
  const failed = useProductsFailed();
  const entry = useProductEntry(product);
  const { target } = useConfigServiceAdapter();
  const { hosts, frameContext } = useShellSlotHosts();
  const { contribution, setContribution } = useTabActionsHost();
  const [openKey, setOpenKey] = useState3(null);
  const { palette } = useTheme();
  const shellContext = useMemo3(
    () => ({ ...frameContext, setTabActions: setContribution, openProductAction: setOpenKey }),
    [frameContext, setContribution]
  );
  const closeDialog = useCallback3(() => setOpenKey(null), []);
  const onCreated = useCallback3(() => {
    setOpenKey(null);
    Promise.resolve(contribution?.sync?.()).catch((error) => console.error("Error refetching after create:", error));
  }, [contribution]);
  const canConfigure = capabilityGate(entry, "configure").kind === "render";
  const canViewHistory = entry === void 0 || capabilityGate(entry, "viewHistory").kind === "render";
  const allTabs = useMemo3(
    () => resolveLayoutTabs(routing.routes, product, tabs, { canViewHistory, canConfigure, hasSettingsTarget: target !== null }),
    [routing.routes, product, tabs, target, canConfigure, canViewHistory]
  );
  const shellTabs = useMemo3(() => toShellTabs(allTabs), [allTabs]);
  const onTabChange = useCallback3(
    (key) => {
      const path = tabPath(allTabs, key);
      if (path) navigate(path);
    },
    [allTabs, navigate]
  );
  const onStateAction = useCallback3(
    (action) => followStateAction(resolveStateAction(action, routing.actionRoutes, routing.salesMailto), navigate),
    [navigate, routing]
  );
  const model = useMemo3(
    () => resolveHeaderActions({
      definition: actions,
      entry,
      tab: contribution ? { ...contribution, sync: withSyncFeedback(contribution.sync, notify) } : null,
      open: setOpenKey
    }),
    [actions, entry, contribution, notify]
  );
  const fill = routing.isFullBleedPath(location.pathname);
  const band = { variant: "fullscreen" };
  if (!isProductsFinal({ productsLoaded: loaded, productsPresent: products !== void 0, productsFailed: failed })) {
    return /* @__PURE__ */ jsx6(
      ProductShell,
      {
        pending: true,
        identity: resolveIdentity(product, void 0, palette),
        tabs: shellTabs,
        LinkComponent: Link,
        advertiseContent: PRODUCT_ADVERTISE_CONTENT[product],
        ...band
      }
    );
  }
  if (failed) return /* @__PURE__ */ jsx6(ProductsFailedFallback, {});
  const access = toShellAccess(entry);
  if (!access.capabilities.visible) {
    return /* @__PURE__ */ jsx6(RestrictedAccessFallback, {});
  }
  const identity = resolveIdentity(product, entry, palette);
  return /* @__PURE__ */ jsxs2(Fragment3, { children: [
    /* @__PURE__ */ jsx6(
      ProductShell,
      {
        access: { ...access, actions: withContactSales(access.state, access.actions) },
        identity,
        actions: model,
        LinkComponent: Link,
        banner: /* @__PURE__ */ jsxs2(Fragment3, { children: [
          banner,
          hosts.banner
        ] }),
        suppressStateBanner,
        aboveTabs: hosts.aboveTabs,
        tabs: shellTabs,
        activeKey: resolveActiveTabKey(allTabs, location.pathname),
        onTabChange,
        onStateAction,
        marketing,
        advertiseContent: PRODUCT_ADVERTISE_CONTENT[product],
        fill,
        ...band,
        children: /* @__PURE__ */ jsx6(ShellFrameContext.Provider, { value: shellContext, children: /* @__PURE__ */ jsx6(Outlet, {}) })
      }
    ),
    access.state !== "marketing" && /* @__PURE__ */ jsx6(ProductCreationDialogs_default, { definition: actions, openKey, onClose: closeDialog, onCreated })
  ] });
};

// src/shell/ProductSettingsRoute.tsx
import React9 from "react";
import { Box as Box3 } from "@mui/material";

// src/settings/ProductSettingsTab.tsx
import { Alert, Box as Box2, LinearProgress, Typography } from "@mui/material";

// src/settings/useSettingsView.ts
import { useCallback as useCallback4, useEffect as useEffect2, useMemo as useMemo4, useState as useState4 } from "react";
function applyWrite(rows, result) {
  if (!rows) return rows;
  return rows.map(
    (row) => row.key === result.key ? {
      ...row,
      currentValue: result.storedValue,
      effectiveValue: result.effectiveValue,
      sourceLevel: result.sourceLevel
    } : row
  );
}
function messageOf(thrown) {
  return thrown instanceof Error ? thrown.message : String(thrown);
}
function useSettingsView(adapter, target) {
  const [settings, setSettings] = useState4(null);
  const [loading, setLoading] = useState4(true);
  const [error, setError] = useState4(null);
  const [savingKeys, setSavingKeys] = useState4([]);
  const level = target.level;
  const levelId = target.levelId;
  const stableTarget = useMemo4(() => ({ level, levelId }), [level, levelId]);
  useEffect2(() => {
    let live = true;
    setLoading(true);
    setError(null);
    adapter.loadSettingsView(stableTarget).then((rows) => {
      if (!live) return;
      setSettings(rows);
      setLoading(false);
    }).catch((thrown) => {
      if (!live) return;
      setError(messageOf(thrown));
      setSettings([]);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [adapter, stableTarget]);
  const runWrite = useCallback4(
    (key, write) => {
      setSavingKeys((keys) => keys.includes(key) ? keys : [...keys, key]);
      setError(null);
      write().then((result) => setSettings((rows) => applyWrite(rows, result))).catch((thrown) => setError(messageOf(thrown))).finally(() => setSavingKeys((keys) => keys.filter((saving) => saving !== key)));
    },
    []
  );
  const save = useCallback4(
    (key, changes) => runWrite(key, () => adapter.saveField(stableTarget, key, changes)),
    [adapter, stableTarget, runWrite]
  );
  const clear = useCallback4(
    (key) => runWrite(key, () => adapter.clearOverride(stableTarget, key)),
    [adapter, stableTarget, runWrite]
  );
  return { settings, loading, error, savingKeys, save, clear };
}

// src/settings/ProductSettingsTab.tsx
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var EMPTY_STATE = /* @__PURE__ */ jsx7(Typography, { variant: "body2", sx: { color: "text.secondary" }, children: "There are no settings you can view for this product." });
var ProductSettingsTab = ({ product, adapter, target, SettingsPanel, hostServices }) => {
  const keys = settingsKeysFor(product);
  const { settings, loading, error, savingKeys, save, clear } = useSettingsView(adapter, target);
  if (loading) return /* @__PURE__ */ jsx7(LinearProgress, {});
  return /* @__PURE__ */ jsxs3(Box2, { sx: { maxWidth: 720 }, children: [
    error && /* @__PURE__ */ jsx7(Alert, { severity: "error", sx: { mb: 2 }, children: error }),
    /* @__PURE__ */ jsx7(
      SettingsPanel,
      {
        settings: settings ?? [],
        keys,
        onSave: save,
        onClear: clear,
        savingKeys,
        emptyState: EMPTY_STATE,
        ...hostServices ? { hostServices } : {}
      }
    )
  ] });
};
var ProductSettingsTab_default = ProductSettingsTab;

// src/shell/Redirect.tsx
import { useEffect as useEffect3 } from "react";

// src/shell/redirectTarget.ts
function redirectTarget(to, carry = {}) {
  return { pathname: to, search: carry.search ?? "", hash: carry.hash ?? "" };
}

// src/shell/Redirect.tsx
var Redirect = ({ to, search, hash }) => {
  const navigate = usePortalRouter().useNavigate();
  useEffect3(() => {
    navigate(redirectTarget(to, { search, hash }), { replace: true });
  }, [navigate, to, search, hash]);
  return null;
};
var Redirect_default = Redirect;

// src/shell/ProductSettingsRoute.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var ProductSettingsRoute = ({ product }) => {
  const canConfigure = capabilityGate(useProductEntry(product), "configure").kind === "render";
  const { adapter, target } = useConfigServiceAdapter();
  const { components, routing, settings } = usePortalHost();
  const { search, hash } = usePortalRouter().useLocation();
  const SettingsPanel = components.SettingsPanel;
  const offerHostServices = settings.hostServices;
  const hostServices = React9.useMemo(
    () => target && offerHostServices ? offerHostServices(target) : void 0,
    [offerHostServices, target]
  );
  if (!canConfigure || target === null) {
    return /* @__PURE__ */ jsx8(Redirect_default, { to: routing.routes.productRoot[product], search, hash });
  }
  return /* @__PURE__ */ jsx8(Box3, { sx: { p: 3 }, children: /* @__PURE__ */ jsx8(
    ProductSettingsTab_default,
    {
      product,
      adapter,
      target,
      SettingsPanel,
      hostServices
    }
  ) });
};
var ProductSettingsRoute_default = ProductSettingsRoute;

// src/shell/LegacyRedirect.tsx
import { LinearProgress as LinearProgress2 } from "@mui/material";

// src/shell/legacyRedirects.ts
function fillRouteParams(pattern, params) {
  return pattern.split("/").map((segment) => {
    if (!segment.startsWith(":")) return segment;
    const value = params[segment.slice(1)];
    return value === void 0 ? segment : encodeURIComponent(value);
  }).join("/");
}

// src/shell/LegacyRedirect.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var LegacyRedirect = ({ to }) => {
  const router = usePortalRouter();
  const params = router.useParams();
  const { search, hash } = router.useLocation();
  const productsLoaded = useProductsLoaded();
  const productsPresent = usePortalProductsState() !== void 0;
  const productsFailed = useProductsFailed();
  if (!isProductsFinal({ productsLoaded, productsPresent, productsFailed })) return /* @__PURE__ */ jsx9(LinearProgress2, {});
  return /* @__PURE__ */ jsx9(Redirect_default, { to: fillRouteParams(to, params), search, hash });
};
var LegacyRedirect_default = LegacyRedirect;

// src/shell/FeatureAccessGate.tsx
import { LinearProgress as LinearProgress3 } from "@mui/material";
import { Fragment as Fragment4, jsx as jsx10 } from "react/jsx-runtime";
var FeatureAccessGate = ({ available, hostReady, children }) => {
  const productsLoaded = useProductsLoaded();
  const productsPresent = usePortalProductsState() !== void 0;
  const productsFailed = useProductsFailed();
  switch (resolveFeatureAccess({ available, hostReady, productsLoaded, productsPresent, productsFailed })) {
    case "allowed":
      return /* @__PURE__ */ jsx10(Fragment4, { children });
    case "pending":
      return /* @__PURE__ */ jsx10(LinearProgress3, {});
    case "failed":
      return /* @__PURE__ */ jsx10(ProductsFailedFallback, {});
    case "restricted":
      return /* @__PURE__ */ jsx10(RestrictedAccessFallback, {});
  }
};
var FeatureAccessGate_default = FeatureAccessGate;

// src/products/moves/resolveValetTabs.ts
var SCHEDULE = "schedule";
var RIDESHARES = "rideshares";
var VALET_GATED_TAB_KEYS = ["schedule", "team", "rideshares", "reports", "missing-ref"];
function valetGatedTabs(routes) {
  return VALET_GATED_TAB_KEYS.map((key) => ({ key, path: routes.movesTabPath(key) }));
}
function resolveValetTabs(routes, entry, options) {
  const root = routes.productRoot.moves;
  const gatedPath = Object.fromEntries(valetGatedTabs(routes).map((tab) => [tab.key, tab.path]));
  const capabilities = entry?.capabilities;
  const selfManaged = isSelfManagedCapable(entry?.operationalModel?.default);
  const tabs = [{ key: "orders", label: "Orders", path: root }];
  if (capabilities?.[SCHEDULE] === true) tabs.push({ key: "schedule", label: "Schedule", path: gatedPath.schedule });
  if (selfManaged) tabs.push({ key: "team", label: "Team", path: gatedPath.team });
  if (capabilities?.[RIDESHARES] === true && selfManaged) {
    tabs.push({ key: "rideshares", label: "Rideshares", path: gatedPath.rideshares });
  }
  if (options?.reportingEnabled) {
    tabs.push({ key: "reports", label: "Reports", path: gatedPath.reports });
  }
  if (options?.missingRefLabel) {
    tabs.push({ key: "missing-ref", label: options.missingRefLabel, path: gatedPath["missing-ref"] });
  }
  return tabs;
}
function hasValetTab(tabs, key) {
  return tabs.some((tab) => tab.key === key);
}

// src/products/moves/resolveValetActions.tsx
import { BiImport, BiPlus, BiSolidAddToQueue } from "react-icons/bi";
import { jsx as jsx11 } from "react/jsx-runtime";
function resolveValetActions(routes, { csvImportEnabled }) {
  const definition = {
    menuLabel: "New Moves",
    menuIcon: /* @__PURE__ */ jsx11(BiPlus, {}),
    createUnavailableText: "Planning moves is unavailable for this rooftop",
    // Priority order: the landing tab's CTA first.
    creation: [{ key: "plan", label: "Plan Moves", icon: /* @__PURE__ */ jsx11(BiSolidAddToQueue, {}), to: routes.planMovesPath() }]
  };
  if (csvImportEnabled) {
    definition.creation.push({ key: "import", label: "Import Moves", icon: /* @__PURE__ */ jsx11(BiImport, {}), to: routes.importMovesPath() });
  }
  return definition;
}

// src/products/appraisals/resolveAppraisalsTabs.ts
function resolveAppraisalsTabs(routes) {
  return [{ key: "index", label: "Appraisal Index", path: routes.productRoot.appraisals }];
}

// src/products/guest-rides/resolveGuestRidesTabs.ts
function resolveGuestRidesTabs(routes) {
  return [{ key: "rides", label: "Rides", path: routes.productRoot["guest-rides"] }];
}

// src/products/guest-rides/requestRideAction.ts
var REQUEST_RIDE_ACTION_KEY = "request-ride";

// src/products/mobile-service/resolveMobileServiceTabs.ts
function mobileServiceGatedTabs(routes) {
  return [{ key: "missing-ref", path: `${routes.productRoot["mobile-service"]}/missing-ref` }];
}
function resolveMobileServiceTabs(routes, options) {
  const root = routes.productRoot["mobile-service"];
  const tabs = [
    { key: "appointments", label: "Appointments", path: root },
    { key: "runs", label: "Runs", path: `${root}/runs` },
    { key: "map", label: "Map", path: `${root}/map` },
    { key: "service-vehicles", label: "Vehicles", path: `${root}/service-vehicles` }
  ];
  if (options?.missingRefLabel) {
    const [missingRef] = mobileServiceGatedTabs(routes);
    tabs.push({ ...missingRef, label: options.missingRefLabel });
  }
  return tabs;
}

// src/products/parts-delivery/resolvePartsDeliveryTabs.ts
function resolvePartsDeliveryTabs(routes, inboundReturns) {
  const root = routes.productRoot["parts-delivery"];
  return [
    { key: "pending", label: "Pending Orders", path: root },
    { key: "live", label: "Active Runs", path: `${root}/live` },
    { key: "templates", label: "Templates", path: `${root}/templates` },
    {
      key: "returns",
      label: "Returns",
      path: `${root}/returns`,
      badge: inboundReturns > 0 ? inboundReturns : void 0
    },
    { key: "service-vehicles", label: "Vehicles", path: `${root}/service-vehicles` }
  ];
}

// src/counts/productCounts.ts
var BUCKET_KEYS = ["upcoming", "active", "completed", "cancelled"];
var EMPTY_BUCKETS = { upcoming: 0, active: 0, completed: 0, cancelled: 0, total: 0 };
var sumBuckets = (list) => list.reduce(
  (sum, buckets) => ({
    upcoming: sum.upcoming + buckets.upcoming,
    active: sum.active + buckets.active,
    completed: sum.completed + buckets.completed,
    cancelled: sum.cancelled + buckets.cancelled,
    total: sum.total + buckets.total
  }),
  EMPTY_BUCKETS
);

// src/counts/countFilters.ts
var customerScopeFilter = (customerIds) => ({ customer_id: { _in: customerIds } });
var windowFilter = (column, window2) => window2 ? { [column]: { _gte: window2.startIso, _lte: window2.endIso } } : {};
var ridesWindowFilter = (window2) => window2 ? {
  _or: [
    { created_at: { _gte: window2.startIso, _lte: window2.endIso } },
    { ridehails: { created_at: { _gte: window2.startIso, _lte: window2.endIso } } }
  ]
} : {};
var usableCustomerIds = (customerIds) => customerIds.filter((id) => !!id);

// src/counts/mapAggregateCounts.ts
var countOf = (block) => block?.aggregate?.count ?? 0;
var withTotal = (buckets) => ({
  ...buckets,
  total: buckets.upcoming + buckets.active + buckets.completed + buckets.cancelled
});
var tallyByCustomer = (rows) => {
  var _a;
  const partial = {};
  for (const key of BUCKET_KEYS) {
    for (const row of rows[key] ?? []) {
      if (row.customer_id === null || row.customer_id === void 0) continue;
      const current = partial[_a = row.customer_id] ?? (partial[_a] = { upcoming: 0, active: 0, completed: 0, cancelled: 0 });
      current[key] += 1;
    }
  }
  return Object.fromEntries(Object.entries(partial).map(([id, buckets]) => [id, withTotal(buckets)]));
};
var mapAggregateCounts = (data, rows, product, unit) => ({
  product,
  unit,
  buckets: data ? withTotal({ upcoming: countOf(data.upcoming), active: countOf(data.active), completed: countOf(data.completed), cancelled: countOf(data.cancelled) }) : EMPTY_BUCKETS,
  truncated: false,
  ...data?.deleted !== void 0 ? { deleted: countOf(data.deleted) } : {},
  ...rows ? { byCustomer: tallyByCustomer(rows) } : {}
});

// src/counts/countDocumentPins.ts
import { Kind } from "graphql";
var operation = (document) => {
  const definition = document.definitions.find((candidate) => candidate.kind === Kind.OPERATION_DEFINITION);
  if (!definition || definition.kind !== Kind.OPERATION_DEFINITION) throw new Error("no operation");
  return definition;
};
var argument = (field, name) => field.arguments?.find((candidate) => candidate.name.value === name);
var literal = (value) => {
  switch (value.kind) {
    case Kind.STRING:
    case Kind.ENUM:
      return value.value;
    case Kind.INT:
    case Kind.FLOAT:
      return Number(value.value);
    case Kind.BOOLEAN:
      return value.value;
    case Kind.NULL:
      return null;
    case Kind.LIST:
      return value.values.map(literal);
    case Kind.OBJECT:
      return Object.fromEntries(value.fields.map((field) => [field.name.value, literal(field.value)]));
    case Kind.VARIABLE:
      return `$${value.name.value}`;
    default:
      return void 0;
  }
};
var readCountBlocks = (document) => operation(document).selectionSet.selections.flatMap((selection) => {
  if (selection.kind !== Kind.FIELD) return [];
  const where = argument(selection, "where");
  return [
    {
      alias: selection.alias?.value ?? selection.name.value,
      field: selection.name.value,
      where: where ? literal(where.value) : {}
    }
  ];
});
var statusesOf = (where) => {
  if (!where || typeof where !== "object") return void 0;
  const record = where;
  const status = record.status;
  if (status?._in) return status._in;
  for (const value of Object.values(record)) {
    const candidates = Array.isArray(value) ? value : [value];
    for (const candidate of candidates) {
      const found = statusesOf(candidate);
      if (found) return found;
    }
  }
  return void 0;
};
var summarizeCountDocument = (document) => readCountBlocks(document).map((block) => {
  const and = block.where._and ?? [];
  const productType = block.where.product_type?._eq;
  return {
    alias: block.alias,
    field: block.field,
    productType,
    usesScope: and.includes("$scope"),
    usesWindow: and.includes("$window"),
    statuses: statusesOf(block.where)
  };
});

// src/ui/ProductAccessNotice.tsx
import { Alert as Alert2, Button as Button2 } from "@mui/material";
import { Fragment as Fragment5, jsx as jsx12 } from "react/jsx-runtime";
var ProductAccessNotice = ({ entry }) => {
  const { routing, router } = usePortalHost();
  const navigate = router.useNavigate();
  if (entry.state !== "limited" && entry.state !== "read_only") return null;
  const text = reasonText(entry.reason) ?? (entry.state === "limited" ? "Some actions are currently paused for this product" : "This product is view-only");
  const actions = entry.actions ?? [];
  return /* @__PURE__ */ jsx12(
    Alert2,
    {
      severity: entry.state === "limited" ? "warning" : "info",
      sx: { mb: 2, alignItems: "center" },
      action: actions.length > 0 ? /* @__PURE__ */ jsx12(Fragment5, { children: actions.map((action) => {
        const route = routing.actionRoutes[action.type];
        const label = actionLabel(action.type);
        return route ? /* @__PURE__ */ jsx12(Button2, { color: "inherit", size: "small", onClick: () => navigate(route), children: label }, action.type) : /* @__PURE__ */ jsx12(Button2, { color: "inherit", size: "small", href: routing.salesMailto, children: label }, action.type);
      }) }) : void 0,
      children: text
    }
  );
};
var ProductAccessNotice_default = ProductAccessNotice;
export {
  BUCKET_KEYS,
  CONFIG_ROLES,
  CONTACT_SALES_ACTION,
  EMPTY_BUCKETS,
  EMPTY_HELD_VALET_MODES,
  FeatureAccessGate_default as FeatureAccessGate,
  GET_PRODUCT_ACCESS,
  GetPortalProductInputsDataDocument,
  HIDDEN_CAPABILITIES,
  LegacyRedirect_default as LegacyRedirect,
  ORDER_TYPES,
  ORDER_TYPES_DISPLAY_ORDER,
  PRODUCT_ACCESS_KEYS,
  PRODUCT_ADVERTISE_CONTENT,
  PRODUCT_IDENTITY,
  PRODUCT_KEYS,
  PRODUCT_LABELS,
  PRODUCT_PORTAL_DEFINITIONS,
  PRODUCT_SETTINGS_KEYS,
  PortalHostProvider,
  PortalProductsLoader,
  ProductAccessNotice_default as ProductAccessNotice,
  ProductCreationDialogs_default as ProductCreationDialogs,
  ProductSettingsRoute_default as ProductSettingsRoute,
  ProductSettingsTab_default as ProductSettingsTab,
  ProductShellLayout,
  ProductsFailedFallback,
  REQUESTED_PRODUCT_KEYS,
  REQUEST_RIDE_ACTION_KEY,
  RestrictedAccessFallback,
  SALES_EMAIL,
  SALES_MAILTO,
  SETTINGS_TAB_KEY,
  SNAPSHOT_MAX_AGE_MS,
  ShellFrameContext,
  ShellSlot,
  VALET_GATED_TAB_KEYS,
  VALET_KEY_OPERATIONAL_MODE,
  VALET_PRODUCT_KEYS,
  actionLabel,
  assembleLocalInputs,
  buildContext,
  capabilityGate,
  composePortalProducts,
  configFlagIsOn,
  createApolloConfigServicePort,
  createApolloProductFetchers,
  customerScopeFilter,
  deriveProducts,
  emptyLocalProductData,
  fillRouteParams,
  followStateAction,
  getPortalProductInputsData,
  hasValetTab,
  heldValetModes,
  isMoveDetailSegment,
  isPortalAccessSettled,
  isProductAccessGranted,
  isProductHeld,
  isProductOffered,
  isProductOwned,
  isProductRouteMounted,
  isProductShown,
  isProductsFinal,
  isSelfManagedCapable,
  mapAggregateCounts,
  mobileServiceGatedTabs,
  openSalesContact,
  pathIsUnder,
  plannerIsUnlocked,
  portalProductsStore,
  prettifyKey,
  productAccentColor,
  productDescription,
  productHasSettings,
  productIdentity,
  productName,
  readCountBlocks,
  readDottedPath,
  readProductSnapshot,
  reasonText,
  resetEarlyProductsFetch,
  resetPortalProducts,
  resolveActiveTabKey,
  resolveAppraisalsTabs,
  resolveFeatureAccess,
  resolveGuestRidesTabs,
  resolveHeaderActions,
  resolveIdentity,
  resolveLayoutTabs,
  resolveMobileServiceTabs,
  resolveMovesLabel,
  resolvePartsDeliveryTabs,
  resolveStateAction,
  resolveValetActions,
  resolveValetOperationalModel,
  resolveValetTabs,
  retryProducts,
  ridesWindowFilter,
  salesMailto,
  selectEnvelope,
  setPortalProducts,
  setProductInputs,
  setProductsFailed,
  setProductsLoaded,
  settingsKeysFor,
  settingsTabRoute,
  snapshotCanCreate,
  snapshotFacts,
  snapshotKey,
  snapshotSelfManaged,
  startEarlyProductsFetch,
  statusesOf,
  sumBuckets,
  summarizeCountDocument,
  tabPath,
  takeEarlyProductsFetch,
  toConfigRole,
  toShellAccess,
  toShellTabs,
  unmountedTabs,
  usableCustomerIds,
  useConfigServiceAdapter,
  useOpenProductAction,
  usePortalHost,
  usePortalProducts,
  usePortalProductsState,
  usePortalRouter,
  usePortalViewer,
  useProductEntry,
  useProductInputs,
  useProductsFailed,
  useProductsLoaded,
  useProductsRetryNonce,
  useRooftopProductFacts,
  useRooftopProductSnapshot,
  useSettingsView,
  useShellFrame,
  useShellSlotHosts,
  useShellTabActions,
  useTabActionsHost,
  useValetSelfManaged,
  useViewerFeature,
  valetGatedTabs,
  windowFilter,
  withContactSales,
  withEmptyOnFailure,
  withSettingsTab,
  withSyncFeedback,
  writeProductSnapshot
};
//# sourceMappingURL=index.mjs.map